import React, { useMemo } from 'react';
import { SearchSelect } from 'src/components/BeaconV2Components/ExperimentalLayout/Settings/Presentations/components/SearchSelect';
import { FormValue } from 'src/components/BeaconV2Components/ExperimentalLayout/Settings/Presentations/types';
import { useAppSelector } from 'src/utils/Hooks';
import { usePresentationFormContext } from 'src/components/BeaconV2Components/ExperimentalLayout/Settings/Presentations/PresentationFormContext';
import { getFieldConfig } from 'src/components/BeaconV2Components/ExperimentalLayout/Settings/Presentations/constants/formConfigs';

interface SubCategorySelectProps {
  selectedCategoryIds: FormValue[];
  selectionOrder: 'category' | 'subcategory' | null;
  onSelectionChange: (newValue: FormValue[]) => void;
}

export const SubCategorySelect = ({ selectedCategoryIds, selectionOrder, onSelectionChange }: SubCategorySelectProps) => {
  const {
    selections: { control },
    currentFormConfig
  } = usePresentationFormContext();
  const subCategories = useAppSelector((state) => state.subCategories);
  
  // Get field configuration
  const subcategoryConfig = getFieldConfig(currentFormConfig, 'subcategoryIds');
  const subcategoriesByCategory = useMemo(() => {
    if (selectionOrder === 'category' && selectedCategoryIds.length > 0) {
      return subCategories.filter((sc) => {
        return selectedCategoryIds.some((selectedCat) => selectedCat.id === sc.parentCategoryId.toString());
      });
    }
    return subCategories;
  }, [subCategories, selectedCategoryIds, selectionOrder]);

  const selectOptions = subcategoriesByCategory.map((subcategory) => ({
    id: subcategory.id,
    value: subcategory.id.toString(),
    displayName: subcategory.displayName
  }));

  // Build dynamic placeholder based on config
  const getPlaceholder = () => {
    const { minCount, maxCount } = subcategoryConfig;
    const hasMin = minCount !== undefined && minCount > 0;
    const hasMax = maxCount !== undefined;
    
    if (hasMin && hasMax) {
      if (minCount === maxCount) {
        return `Select exactly ${minCount} ${minCount === 1 ? 'subcategory' : 'subcategories'}`;
      }
      return `Select ${minCount} to ${maxCount} subcategories`;
    }
    if (hasMin) {
      return `Select at least ${minCount} ${minCount === 1 ? 'subcategory' : 'subcategories'}`;
    }
    if (hasMax) {
      return `Select up to ${maxCount} ${maxCount === 1 ? 'subcategory' : 'subcategories'}`;
    }
    return 'Search or select subcategories you would like to add to your presentation';
  };

  return (
    <SearchSelect
      name="subcategoryIds"
      title="Subcategory Inclusions"
      placeholder={getPlaceholder()}
      selectOptions={selectOptions}
      isMulti={true}
      maxCount={subcategoryConfig.maxCount}
      control={control}
      onSelectionChange={onSelectionChange}
    />
  );
};
